import React, { useState, useEffect } from 'react';
import { useGetTextosQuery, useUpdateTextosMutation } from '../features/textos/api/textosApiSlice';
import { Empresa } from '../features/empresas/types';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectBotPermissions } from '../features/auth/authSlice';
import TextoEditor from '../features/textos/components/TextoEditor';
import { showToast } from '../components/ui/Toast';

export const TextosPage = () => {
  // Obtener el usuario actual y su empresaId
  const currentUser = useSelector(selectCurrentUser);
  const botPermissions = useSelector(selectBotPermissions);
  const empresaId = currentUser?.empresa_id;
  const isSuperAdmin = currentUser?.role === 'superadmin';
  
  // Estados para manejar la edición
  const [editingTexto, setEditingTexto] = useState<{ id: number, botNumber: number, fieldNumber: number, valor: string } | null>(null);
  // Estado para forzar actualizaciones
  const [updateFlag, setUpdateFlag] = useState(Date.now());
  
  // La query devuelve Empresa | undefined
  const { 
    data: empresaData, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useGetTextosQuery(
    empresaId ? { empresaId } : undefined,
    { 
      skip: !empresaId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true
    }
  ) as { 
    data?: Empresa, 
    isLoading: boolean, 
    isError: boolean, 
    error: unknown,
    refetch: () => Promise<{ data?: Empresa }>
  };

  // Depuración: Log del empresaData cuando cambia
  useEffect(() => {
    if (empresaData) {
      console.log('EmpresaData recibido:', empresaData);
      // Verificar textos para bot1
      console.log('Textos Bot 1:', {
        bot1_texto1: empresaData.bot1_texto1,
        bot1_texto2: empresaData.bot1_texto2,
        bot1_texto3: empresaData.bot1_texto3,
        bot1_texto4: empresaData.bot1_texto4,
        bot1_texto5: empresaData.bot1_texto5,
      });
      // Verificar textos para bot10
      console.log('Textos Bot 10:', {
        bot10_texto1: empresaData.bot10_texto1,
        bot10_texto2: empresaData.bot10_texto2,
        bot10_texto3: empresaData.bot10_texto3,
        bot10_texto4: empresaData.bot10_texto4,
        bot10_texto5: empresaData.bot10_texto5,
      });
    }
  }, [empresaData, updateFlag]);

  const [updateTextos, { isLoading: isUpdatingTextos }] = useUpdateTextosMutation();
  
  // Handler para iniciar edición
  const handleEditStart = (botNumber: number, fieldNumber: number) => {
    if (!empresaData) return;
    
    let valorActual = '';
    const botPrefix = `bot${botNumber}`;
    const textoKey = `${botPrefix}_texto${fieldNumber}` as keyof Empresa;
    
    valorActual = empresaData[textoKey] as string || '';
    console.log(`Iniciando edición para ${textoKey}, valor actual:`, valorActual);
    
    setEditingTexto({ id: empresaData.id, botNumber, fieldNumber, valor: valorActual });
  };
  
  // Handler para cancelar edición
  const handleEditCancel = () => {
    setEditingTexto(null);
  };
  
  // Handler para guardar cambios
  const handleSave = async (nuevoTexto?: string) => {
    if (!editingTexto || !empresaId) {
      console.error('No se puede guardar: datos de edición o empresaId faltantes');
      showToast('Error al actualizar el texto: datos incompletos', 'error');
      return;
    }

    try {
      const { botNumber, fieldNumber, id: currentEmpresaId } = editingTexto;
      const fieldKey = `bot${botNumber}_texto${fieldNumber}`;
      const enabledKey = `bot${botNumber}_enabled${fieldNumber}`;
      
      // Asegurarse de que el texto no sea undefined
      const textoFinal = nuevoTexto !== undefined ? nuevoTexto : editingTexto.valor;
      
      // Verificar que el texto no esté vacío o undefined antes de guardar
      if (textoFinal === undefined || textoFinal === null) {
        console.error('Error: El texto a guardar es undefined o null');
        showToast('Error al actualizar: el texto está vacío', 'error');
        return;
      }

      // Siempre habilitar el campo cuando se guarda un texto
      const changes: Record<string, string | boolean> = {
        [fieldKey]: textoFinal,
        [enabledKey]: true, // Asegurarse de que el campo quede habilitado al guardar
        [`bot${botNumber}_enabled`]: true // También habilitar el bot general
      };

      console.log(`Guardando cambios para ${fieldKey}:`, {
        empresaId: currentEmpresaId,
        botNumber,
        fieldNumber,
        textoOriginal: editingTexto.valor,
        textoNuevo: nuevoTexto,
        textoFinal,
        cambios: changes
      });

      // Verificar permisos del usuario actual
      const userHasPermission = isSuperAdmin || (botPermissions && botPermissions[`bot${botNumber}_enabled`]);
      console.log(`Verificación de permisos para bot${botNumber}:`, {
        isSuperAdmin,
        permisoUsuario: botPermissions && botPermissions[`bot${botNumber}_enabled`],
        tienePermiso: userHasPermission
      });

      if (!userHasPermission) {
        console.error(`Error: Usuario no tiene permiso para bot${botNumber}`);
        showToast(`No tienes permiso para editar textos del bot ${botNumber}`, 'error');
        return;
      }

      // Llamar a la API para actualizar usando updateTextos
      console.log('Enviando request con:', { empresaId, changes });
      const resultado = await updateTextos({ 
        empresaId,
        changes
      }).unwrap();

      console.log('Resultado de la actualización:', resultado);
      
      // Aceptar cualquier respuesta como válida
      // Forzar refrescado de datos
      const refetchResult = await refetch();
      console.log('Resultado del refetch:', refetchResult);
      
      setUpdateFlag(Date.now());
      console.log('UpdateFlag actualizado:', Date.now());
      
      // Cerrar editor y mostrar mensaje
      setEditingTexto(null);
      showToast('Texto actualizado con éxito', 'success');
    } catch (err) {
      console.error('Error al actualizar texto:', err);
      
      // Mostrar mensaje de error más específico si está disponible
      let errorMsg = 'Error al actualizar el texto';
      
      if (err && typeof err === 'object') {
        if ('status' in err) {
          const status = (err as {status?: number}).status;
          if (status === 403) {
            errorMsg = 'No tienes permiso para actualizar este texto. Verifica tus permisos de bot.';
          } else if (status === 404) {
            errorMsg = 'No se encontró la empresa para actualizar.';
          } else if (status === 500) {
            errorMsg = 'Error interno del servidor al procesar la solicitud.';
          }
        }
        
        if ('data' in err) {
          const errorData = (err as {data?: {message?: string}}).data;
          if (errorData?.message) {
            errorMsg += `: ${errorData.message}`;
          }
        }
      }
      
      showToast(errorMsg, 'error');
    }
  };

  // Si no hay empresaId (por ejemplo, es un admin sin empresa asignada)
  if (!empresaId) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-6">Configuración de Textos</h1>
        <div className="bg-warning/10 border border-warning/30 text-warning px-4 py-3 rounded-md mb-4">
          <p>Para acceder a los textos, debe seleccionar o estar asignado a una empresa.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-center py-10">Cargando textos...</div>;
  }

  if (isError) {
    return (
      <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-md mb-4">
        <p>Error al cargar textos: {(error as {data?: {message?: string}})?.data?.message || 'Ocurrió un error desconocido'}</p>
      </div>
    );
  }

  if (!empresaData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-6">Configuración de Textos</h1>
        <div className="bg-muted/50 border border-border text-foreground px-4 py-3 rounded-md mb-4">
          <p>No se encontraron textos configurables para su empresa o no están habilitados.</p>
        </div>
      </div>
    );
  }

  // Componente para mostrar un bot
  const BotSection = ({ botNumber }: { botNumber: number }) => {
    if (!empresaData || !currentUser) return null;
    
    const botPrefix = `bot${botNumber}`;
    
    // Verificar si al menos un campo está habilitado para considerar el bot habilitado
    let anyFieldEnabled = false;
    for (let i = 1; i <= 5; i++) {
      const fieldEnabledKey = `${botPrefix}_enabled${i}` as keyof Empresa;
      if (empresaData[fieldEnabledKey] === true) {
        anyFieldEnabled = true;
        break;
      }
    }
    
    // Un bot está habilitado si tiene la propiedad bot{X}_enabled como true 
    // O si tiene al menos un campo habilitado
    const botEnabledKey = `${botPrefix}_enabled` as keyof Empresa;
    const isEnabled = empresaData[botEnabledKey] === true || anyFieldEnabled;
    
    const botName = empresaData[`${botPrefix}_nombre` as keyof Empresa] as string;
    
    // Verificar si el usuario tiene permiso para este bot usando el selector global
    const botKey = `bot${botNumber}_enabled` as keyof typeof botPermissions;
    const userHasPermission = isSuperAdmin || (botPermissions && botPermissions[botKey]);
    
    // Imprimir información de depuración para este bot
    console.log(`Bot ${botNumber} info:`, {
      isEnabled,
      anyFieldEnabled,
      botName,
      userHasPermission,
      botEnabledKey,
      botEnabledValue: empresaData[botEnabledKey],
      userPermissions: botPermissions && botPermissions[botKey]
    });
    
    // Si el bot no está habilitado en la empresa o el usuario no tiene permiso, no mostrar
    if (!isEnabled || !userHasPermission) {
      return null;
    }

    // Preparar un arreglo para almacenar los campos habilitados
    const fields = [];
    
    // Iterar sobre los campos para el bot actual
    for (let fieldNumber = 1; fieldNumber <= 5; fieldNumber++) {
      const enabledKey = `${botPrefix}_enabled${fieldNumber}` as keyof Empresa;
      const nameKey = `${botPrefix}_nombre${fieldNumber}` as keyof Empresa;
      const textoKey = `${botPrefix}_texto${fieldNumber}` as keyof Empresa;
      
      const fieldEnabled = empresaData[enabledKey] === true;
      const fieldName = empresaData[nameKey] as string;
      const fieldValue = empresaData[textoKey] as string;
      
      // Log del campo de texto para depurar
      console.log(`Campo ${botPrefix}_texto${fieldNumber}:`, {
        enabledKey,
        fieldEnabled,
        nameKey,
        fieldName,
        textoKey,
        fieldValue,
        fieldValueType: typeof fieldValue
      });
      
      // Solo mostrar campos que:
      // 1. Estén explícitamente habilitados (enabled=true) 
      // Y 
      // 2. Que tengan un nombre de campo o texto no vacío
      if (!fieldEnabled || 
          ((!fieldName || fieldName.trim() === '') && 
           (!fieldValue || fieldValue.trim() === ''))) {
        continue;
      }
      
      fields.push(
        <div key={`${botPrefix}-field-${fieldNumber}`} className="p-4 bg-background rounded-md border border-border">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-medium text-foreground">{fieldName || `Campo ${fieldNumber}`}</h3>
            <button
              onClick={() => handleEditStart(botNumber, fieldNumber)}
              className="text-primary hover:text-primary/80"
            >
              Editar
            </button>
          </div>
          <p className="text-foreground whitespace-pre-wrap">
            {fieldValue ? fieldValue : 'Sin contenido'}
          </p>
        </div>
      );
    }
    
    // Si no hay campos habilitados, no mostrar el bot
    if (fields.length === 0) {
      return null;
    }

    return (
      <div className="mb-8 p-6 bg-card rounded-lg border border-border">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          {botName || `Bot ${botNumber}`}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields}
        </div>
      </div>
    );
  };

  // Bots para los que el usuario tiene permiso
  const userEnabledBots = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].filter(botNumber => {
    if (isSuperAdmin) return true;
    const botKey = `bot${botNumber}_enabled` as keyof typeof botPermissions;
    return botPermissions && botPermissions[botKey];
  });
  
  // Si el usuario no tiene permisos para ningún bot
  if (userEnabledBots.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-6">Configuración de Textos</h1>
        <div className="bg-muted/50 border border-border text-foreground px-4 py-3 rounded-md mb-4">
          <p>No tiene acceso a ningún bot. Contacte al administrador para solicitar permisos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">Configuración de Textos</h1>
        <button 
          onClick={() => {
            console.log('Refrescando datos manualmente...');
            refetch();
            setUpdateFlag(Date.now());
            showToast('Datos actualizados', 'success');
          }}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          disabled={isLoading}
        >
          {isLoading ? 'Actualizando...' : 'Refrescar'}
        </button>
      </div>
      
      {/* Sección de Bots */}
      <div className="space-y-6">
        {userEnabledBots.map((botNumber) => (
          <BotSection key={`bot-${botNumber}`} botNumber={botNumber} />
        ))}
      </div>

      {/* Modal de edición */}
      {editingTexto && (
        <TextoEditor
          texto={editingTexto.valor}
          onSave={handleSave}
          onCancel={handleEditCancel}
          isLoading={isUpdatingTextos}
        />
      )}
    </div>
  );
}; 